import * as THREE from "three";

import type { AudioFeatures } from "../audio/types";
import type { AltSceneApi, CameraPersonality } from "./altScenes";
import type { BioclastParams, QualityTier } from "./scenes/bioclast";

export type LiveMathParticleMode =
  | "radialPulse"
  | "superform"
  | "spiralVortex"
  | "hopfFibration"
  | "fibonacciSequence"
  | "plasmaMembrane"
  | "noiseBlossom"
  | "livingShell"
  // Living Forms cores (Immersiom-inspired). Branching, cellular, fracturing
  // and self-intersecting topologies that the original eight modes lack.
  | "mycelialBloom"
  | "chladniShell"
  | "coralSkin"
  | "gyroidLattice"
  | "torusKnotWeave"
  | "cymaticBell"
  | "voronoiShatter"
  | "kleinRibbon"
  // Living Forms wave 2. Instability, field-line, attractor and shedding
  // behaviours - motion signatures the first twelve cores do not cover.
  | "ferrofluidCrown"
  | "magneticCurtain"
  | "fractalFlame"
  | "vortexCascade"
  // Living Forms wave 3. Advected turbulence and damped oscillation.
  | "smokeColumn"
  | "harmonograph";

const MODE_INDEX: Record<LiveMathParticleMode, number> = {
  radialPulse: 0,
  superform: 1,
  spiralVortex: 2,
  hopfFibration: 3,
  fibonacciSequence: 4,
  plasmaMembrane: 5,
  noiseBlossom: 6,
  livingShell: 7,
  mycelialBloom: 8,
  chladniShell: 9,
  coralSkin: 10,
  gyroidLattice: 11,
  torusKnotWeave: 12,
  cymaticBell: 13,
  voronoiShatter: 14,
  kleinRibbon: 15,
  ferrofluidCrown: 16,
  magneticCurtain: 17,
  fractalFlame: 18,
  vortexCascade: 19,
  smokeColumn: 20,
  harmonograph: 21,
};

const LIVING_FORM_MODES = new Set<LiveMathParticleMode>([
  "superform",
  "plasmaMembrane",
  "noiseBlossom",
  "livingShell",
  "mycelialBloom",
  "chladniShell",
  "coralSkin",
  "gyroidLattice",
  "torusKnotWeave",
  "cymaticBell",
  "voronoiShatter",
  "kleinRibbon",
  "ferrofluidCrown",
  "magneticCurtain",
  "fractalFlame",
  "vortexCascade",
  "smokeColumn",
  "harmonograph",
]);

const MAX_PARTICLES = 120_000;

type UniformMap = Record<string, THREE.IUniform<number>> & {
  uTime: THREE.IUniform<number>;
  uCount: THREE.IUniform<number>;
  uMode: THREE.IUniform<number>;
  uHue: THREE.IUniform<number>;
  uSaturation: THREE.IUniform<number>;
  uOpacity: THREE.IUniform<number>;
  uIntensity: THREE.IUniform<number>;
  uPointSize: THREE.IUniform<number>;
  uAmplitude: THREE.IUniform<number>;
  uFrequency: THREE.IUniform<number>;
  uRotation: THREE.IUniform<number>;
  uDepthScale: THREE.IUniform<number>;
  uWarmth: THREE.IUniform<number>;
  uColorSpread: THREE.IUniform<number>;
  uColorFlow: THREE.IUniform<number>;
  uOutputSpread: THREE.IUniform<number>;
  uOutputSpreadY: THREE.IUniform<number>;
  uBass: THREE.IUniform<number>;
  uMid: THREE.IUniform<number>;
  uTreble: THREE.IUniform<number>;
  uBeat: THREE.IUniform<number>;
  uFlow: THREE.IUniform<number>;
};

function hashUnit(value: number): number {
  const result = Math.sin(value) * 43758.5453123;
  return result - Math.floor(result);
}

function smoothBeat(prev: number, beat: number, dt: number): number {
  if (beat > prev) return beat;
  return prev * Math.exp(-dt * 5);
}

function hsl(h: number, s: number, l: number): THREE.Color {
  const color = new THREE.Color();
  color.setHSL(((h % 360) + 360) % 360 / 360, s, l);
  return color;
}

function qualityCap(tier: QualityTier): number {
  switch (tier) {
    case "mobile": return 30_000;
    case "standard": return 60_000;
    case "high": return 85_000;
    case "ultra": return 110_000;
    case "lab": return MAX_PARTICLES;
  }
}

const VERTEX_SHADER = `
  precision highp float;
  attribute float aIndex;
  attribute vec2 aSeed;
  uniform float uTime;
  uniform float uCount;
  uniform float uMode;
  uniform float uHue;
  uniform float uSaturation;
  uniform float uOpacity;
  uniform float uIntensity;
  uniform float uPointSize;
  uniform float uAmplitude;
  uniform float uFrequency;
  uniform float uRotation;
  uniform float uDepthScale;
  uniform float uWarmth;
  uniform float uColorSpread;
  uniform float uColorFlow;
  uniform float uOutputSpread;
  uniform float uOutputSpreadY;
  uniform float uBass;
  uniform float uMid;
  uniform float uTreble;
  uniform float uBeat;
  uniform float uFlow;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vEnergy;
  varying float vSparkle;
#ifdef USE_INSTANCED_BILLBOARD
  varying vec2 vSpriteUv;
#endif

  const float TAU = 6.28318530718;
  const float PI = 3.14159265359;
  const float GOLDEN_ANGLE = 2.39996322973;

  vec3 hsl2rgb(vec3 c) {
    vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
  }

  vec3 referencePalette(float x, float energy, float warmthBias) {
    float warmth = clamp(uWarmth + warmthBias, 0.0, 1.0);
    vec3 cool = hsl2rgb(vec3(fract(uHue + 0.58 * uColorSpread), 0.72 * uSaturation, 0.48));
    vec3 amber = hsl2rgb(vec3(fract(uHue + 0.08 * uColorSpread), 0.82 * uSaturation, 0.56));
    vec3 hot = hsl2rgb(vec3(fract(uHue + 0.98 * uColorSpread), 0.92 * uSaturation, 0.62));
    vec3 magenta = hsl2rgb(vec3(fract(uHue + 0.78 * uColorSpread), 0.88 * uSaturation, 0.58));
    vec3 base = mix(cool, amber, smoothstep(0.18, 0.78, warmth));
    vec3 hi = mix(amber, hot, clamp(energy * 0.6, 0.0, 1.0));
    vec3 color = mix(base, hi, smoothstep(0.32, 0.92, x));
    color = mix(color, magenta, smoothstep(0.62, 0.94, x) * (0.22 + uTreble * 0.18));
    return mix(vec3(dot(color, vec3(0.2126, 0.7152, 0.0722))), color, uSaturation);
  }

  mat2 rot(float a) {
    float s = sin(a);
    float c = cos(a);
    return mat2(c, -s, s, c);
  }

  vec3 rotateY(vec3 p, float a) {
    p.xz = rot(a) * p.xz;
    return p;
  }

  vec3 rotateX(vec3 p, float a) {
    p.yz = rot(a) * p.yz;
    return p;
  }

  float superRadius(float a, float m, float n1, float n2, float n3) {
    float c = abs(cos(m * a * 0.25));
    float s = abs(sin(m * a * 0.25));
    return pow(max(0.0001, pow(c, n2) + pow(s, n3)), -1.0 / max(0.15, n1));
  }

  vec4 radialPulse(float t, vec2 seed) {
    float y0 = 1.0 - 2.0 * t;
    float r0 = sqrt(max(0.0, 1.0 - y0 * y0));
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.012 + uRotation * 0.05);

    float topMask = smoothstep(0.0, 1.0, y0);
    float bowlMask = 1.0 - smoothstep(-1.0, 0.0, y0);
    float coneRadius = mix(1.0, 0.04, topMask);
    float bowlRadius = mix(1.0, 1.05, bowlMask);
    float radialScale = coneRadius * bowlRadius;

    float stemPull = pow(topMask, 2.0) * 0.55;
    float yScale = mix(0.85, 0.85 + stemPull * 1.4, 1.0);

    float rd = 1.0 - r0;
    float ringPhase = rd * TAU * (uFrequency * 1.6 + uMid * 2.2) - uTime * (0.85 + uFlow * 0.4);
    float ring = sin(ringPhase + uBass * 1.2);
    float fineRing = sin(rd * TAU * (12.0 + uFrequency * 2.0) + uTime * 1.7) * uTreble;

    float radius = 22.0;
    vec3 p;
    p.x = cos(theta) * r0 * radius * radialScale;
    p.z = sin(theta) * r0 * radius * radialScale;
    p.y = y0 * radius * yScale;

    p.xz *= (1.0 + uBass * 0.1 + uBeat * 0.04) *
      (1.0 + ring * uAmplitude * (0.12 + uMid * 0.05) + fineRing * 0.025);
    p.y += ring * uAmplitude * (2.0 + uMid * 1.4) * (0.4 + bowlMask * 0.6);
    p.y += fineRing * 0.35;

    return vec4(p, topMask);
  }

  vec4 superform(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float ring = sqrt(max(0.0, 1.0 - y * y));
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.018 + uFlow * 0.055);
    float top = smoothstep(0.04, 0.94, y);
    float bowl = 1.0 - smoothstep(-0.92, -0.05, y);
    float morph = sin(uTime * 0.11) * 0.5 + 0.5;
    float petalA = 4.0 + uFrequency;
    float petalB = 7.0 + uFrequency;
    float petal = mix(cos(theta * petalA), cos(theta * petalB), morph);
    float shapeAmount = clamp(uAmplitude / 0.56, 0.5, 1.35);
    float radialScale = (1.0 - top * 0.72 * shapeAmount) *
      (1.0 + bowl * 0.08 * shapeAmount);
    float pulse = 1.0 + uBass * 0.06 + uBeat * 0.05 +
      petal * (uMid * 0.022 + 0.018);
    float radius = 21.5;
    vec3 p = vec3(
      cos(theta) * ring * radialScale * pulse,
      y * (0.82 + top * 1.5),
      sin(theta) * ring * radialScale * pulse
    ) * radius;
    p.y -= radius * 0.75;
    return vec4(p, top);
  }

  vec4 spiralVortex(float t, vec2 seed) {
    float bucket = mod(aIndex, 8.0);
    float subtype = step(7.0, bucket);

    if (subtype > 0.5) {
      float innerCount = max(1.0, floor(uCount / 8.0));
      float innerIdx = floor(aIndex / 8.0);
      float u = (innerIdx / innerCount) * TAU + uTime * (0.05 + uRotation * 0.08);
      float v = seed.y * TAU;
      float majorRadius = 8.0 * (1.0 + uBass * 0.1 + uBeat * 0.04);
      float minorRadius = 1.3 + uAmplitude * 0.3 + uBeat * 0.20;
      vec3 p;
      p.x = (majorRadius + minorRadius * cos(v)) * cos(u);
      p.z = (majorRadius + minorRadius * cos(v)) * sin(u);
      p.y = minorRadius * sin(v) * 0.45;
      return vec4(p, 1.0);
    } else {
      float cols = 280.0;
      float outerCount = uCount * 0.875;
      float rowsF = max(2.0, floor(outerCount / cols));
      float col = mod(aIndex, cols);
      float row = floor(aIndex / cols);
      float u = (col + seed.x * 0.5) / cols * TAU + uTime * (0.04 + uRotation * 0.06);
      float v = (row + seed.y * 0.5) / rowsF * TAU;

      float majorRadius = 14.0;
      float minorRadius = 7.5 + uAmplitude * 0.6 + uBass * 0.8;

      float lift = sin(u * 3.0 + uTime * 0.4) * (0.6 + uAmplitude * 0.5);
      float twist = sin(v) * (0.4 + uFrequency * 0.15 + uMid * 0.9);
      float corrugation = sin(v * (7.0 + uFrequency) + uTime * 2.1) * uTreble * 0.45;

      vec3 p;
      p.x = (majorRadius + (minorRadius + corrugation) * cos(v)) * cos(u + twist * 0.08);
      p.z = (majorRadius + (minorRadius + corrugation) * cos(v)) * sin(u + twist * 0.08);
      p.y = (minorRadius + corrugation) * sin(v) + lift + uBeat * sin(u * 3.0) * 0.6;

      return vec4(p, 0.0);
    }
  }

  vec4 hopfFibration(float t, vec2 seed) {
    float fiberCount = 64.0;
    float fiberLen = max(96.0, floor(uCount / fiberCount));
    float fiberIdx = mod(floor(aIndex / fiberLen), fiberCount);
    float along = mod(aIndex, fiberLen);
    float s = (along + 0.5) / fiberLen;

    float latBands = 8.0;
    float lonBands = max(3.0, floor(fiberCount / latBands));
    float latIdx = mod(fiberIdx, latBands);
    float lonIdx = floor(fiberIdx / latBands);
    float latT = (latIdx + 0.5) / latBands;
    float lonT = (lonIdx + 0.5) / lonBands;

    float c0 = sin(mix(-1.05, 1.02, latT)) * 0.78;
    float c = clamp(c0 + uMid * 0.16 * sin(uTime * 0.32 + lonIdx * 0.37) + 0.028 * sin(uTime * 0.10 + lonIdx * 0.37), -0.84, 0.80);
    float bphi = lonT * TAU
               + latIdx * GOLDEN_ANGLE * 0.23
               + uTime * (0.030 + uFlow * 0.014)
               + uMid * 0.22
               + 0.045 * sin(uTime * 0.08 + latIdx * 0.9);
    float bs = sqrt(max(0.0, 1.0 - c * c));
    float a = bs * cos(bphi);
    float b = bs * sin(bphi);

    float xi = atan(b, a);

    float streamSpeed = 0.55 + uFlow * 0.34 + uRotation * 0.20;
    float ang = s * TAU
              + uTime * streamSpeed
              + latIdx * 0.19
              + lonIdx * 0.047
              + 0.06 * sin(uTime * 0.14 + lonIdx * 0.31);

    float c1 = sqrt(0.5 * (1.0 + c));
    float c2 = sqrt(0.5 * (1.0 - c));
    float a1 = (ang + xi) * 0.5;
    float a2 = (ang - xi) * 0.5;

    float x4 = c1 * cos(a1);
    float y4 = c1 * sin(a1);
    float z4 = c2 * cos(a2);
    float w4 = c2 * sin(a2);

    float rawDenom = 1.0 - w4;
    float visibility = smoothstep(0.36, 0.86, rawDenom);
    float denom = max(rawDenom, 0.30);
    vec3 p = vec3(x4, y4, z4) * (18.0 / denom);

    float mag = length(p);
    float cap = 58.0;
    if (mag > cap) p *= cap / mag;

    p = rotateX(p, 0.34);
    p = rotateY(p, -0.28);
    float fiberRipple = sin(s * TAU * (6.0 + uFrequency) + uTime * 2.4 + lonIdx * 0.41) * uTreble * 0.025;
    p *= 1.0 + uBass * 0.09 + uBeat * 0.035 + fiberRipple;

    float stripe = pow(0.5 + 0.5 * sin(s * TAU * 5.0 - uTime * (2.4 + uFlow * 0.9) + lonIdx * 0.41), 5.0);
    float family = 0.5 + 0.5 * sin(latIdx * 0.75 + lonIdx * 0.23);
    float subtype = visibility * (0.34 + 0.18 * family + 0.34 * stripe);
    return vec4(p, subtype);
  }

  vec4 fibonacciSequence(float t, vec2 seed) {
    float armCount = 34.0;
    float armIdx = mod(aIndex, armCount);
    float stepIdx = floor(aIndex / armCount);
    float steps = max(1.0, floor(uCount / armCount));
    float drift = uTime * (8.0 + uFlow * 18.0 + uRotation * 8.0);
    float q = fract((stepIdx + drift + armIdx * 0.21) / steps);
    float theta = armIdx * GOLDEN_ANGLE
          + q * TAU * (2.35 + uFrequency * 0.42 + uMid * 0.72)
                + q * q * (1.15 + uDepthScale * 0.55);

    float sqrtQ = sqrt(q);
    float radius = (1.8 + 30.0 * sqrtQ) * (1.0 + uBass * 0.1 + uBeat * 0.035);
    float armWave = sin(armIdx * 0.62 + q * TAU * (5.0 + uFrequency * 0.8) - uTime * (0.8 + uFlow * 0.5));
    float shell = log(1.0 + q * 18.0);

    vec3 p;
    p.x = cos(theta) * radius * (1.0 + armWave * uAmplitude * 0.035);
    p.z = sin(theta) * radius * (1.0 + armWave * uAmplitude * 0.035);
    p.y = (q - 0.5) * (3.8 + uDepthScale * 6.8)
        + sin(theta * 0.13 - uTime * 1.2) * (0.7 + uAmplitude * 0.9);

    float curl = smoothstep(0.16, 1.0, q);
    p.yz = rot((curl - 0.5) * (0.42 + uDepthScale * 0.36)) * p.yz;
    p.xz = rot(shell * 0.26 + uTime * (0.035 + uRotation * 0.08)) * p.xz;

    float rib = pow(0.5 + 0.5 * sin(q * TAU * (13.0 + uTreble * 7.0) + armIdx * GOLDEN_ANGLE - uTime * (1.8 + uFlow)), 3.2);
    rib = max(rib, pow(0.5 + 0.5 * sin(q * TAU * 21.0 - armIdx * 0.74 + uTime * 1.1), 5.0) * 0.72);
    p *= 1.0 + rib * (uAmplitude * 0.018 + uTreble * 0.025);

    return vec4(p, rib);
  }

  vec4 plasmaMembrane(float t, vec2 seed) {
    float columns = 320.0;
    float rows = max(2.0, floor(uCount / columns));
    float column = mod(aIndex, columns);
    float row = floor(aIndex / columns);
    vec2 surface = vec2(
      (column + seed.x * 0.42) / max(1.0, columns - 1.0),
      (row + seed.y * 0.42) / max(1.0, rows - 1.0)
    ) * 2.0 - 1.0;

    float travel = uTime * (0.42 + uFlow * 0.34);
    float distanceFromCenter = length(surface);
    float bassWave = sin(surface.x * 5.2 - travel * 1.25) * (0.7 + uBass * 1.8);
    float midWave = sin(surface.y * 7.4 + surface.x * 2.2 + travel) * (0.55 + uMid * 1.5);
    float crossWave = sin((surface.x - surface.y) * 12.0 - travel * 1.8) * (0.18 + uTreble * 0.55);
    float onsetRing = sin(distanceFromCenter * 18.0 - travel * 2.8) * uBeat * 2.2;
    float taper = 1.0 - smoothstep(0.72, 1.42, distanceFromCenter);

    vec3 p = vec3(
      surface.x * 26.0,
      surface.y * 17.0,
      (bassWave + midWave + crossWave + onsetRing) * uAmplitude * taper
    );
    p.y += sin(surface.x * 2.4 + travel * 0.32) * (0.5 + uMid * 0.8);
    p = rotateX(p, -0.28);

    float crest = clamp(0.5 + (bassWave + midWave + crossWave) * 0.18, 0.0, 1.0);
    return vec4(p, crest);
  }

  vec4 noiseBlossom(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float ring = sqrt(max(0.0, 1.0 - y * y));
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.025 + uRotation * 0.08);
    vec3 normal = vec3(cos(theta) * ring, y, sin(theta) * ring);

    float drift = uTime * (0.34 + uFlow * 0.28);
    float low = sin(normal.x * 4.0 + drift) * sin(normal.y * 3.0 - drift * 0.72);
    float medium = sin(normal.z * 6.0 + normal.x * 2.0 - drift * 1.18);
    float fine = sin((normal.x + normal.y - normal.z) * 13.0 + drift * 2.1 + seed.x * TAU);
    float layeredNoise = low * 0.58 + medium * 0.30 + fine * (0.08 + uTreble * 0.08);
    float bloom = layeredNoise * uAmplitude * (0.16 + uMid * 0.12);
    float pulse = uBass * 0.09 + uBeat * (0.045 + 0.018 * sin(theta * 5.0));
    vec3 p = normal * 21.5 * (1.0 + bloom + pulse);

    float ridge = smoothstep(0.25, 0.88, 0.5 + layeredNoise * 0.5);
    return vec4(p, ridge);
  }

  vec4 livingShell(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float ring = sqrt(max(0.0, 1.0 - y * y));
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.018 + uRotation * 0.055);
    vec3 normal = vec3(cos(theta) * ring, y, sin(theta) * ring);

    float slowTime = uTime * (0.32 + uFlow * 0.16);
    float inhale = sin(slowTime) * (0.045 + uAmplitude * 0.025);
    float lobeA = sin(theta * 3.0 + slowTime * 0.58) * sin(acos(clamp(y, -1.0, 1.0)) * 2.0);
    float lobeB = cos(theta * 2.0 - slowTime * 0.41 + seed.y * 0.35) * (1.0 - y * y);
    float directional = (lobeA * 0.62 + lobeB * 0.38) * uAmplitude * (0.10 + uMid * 0.08);
    float heartbeat = uBeat * (0.055 + 0.022 * sin(theta * 2.0 + y * 4.0));
    float breath = 1.0 + inhale + uBass * 0.085 + directional + heartbeat;
    vec3 p = normal * 21.5 * breath;
    p += vec3(0.0, lobeA * uMid * 0.65, lobeB * uTreble * 0.42);

    float skin = clamp(0.5 + lobeA * 0.28 + lobeB * 0.18, 0.0, 1.0);
    return vec4(p, skin);
  }

  // ---------------------------------------------------------------------
  // Living Forms cores. Shared helpers first.
  // ---------------------------------------------------------------------
  float lfHash11(float p) { return fract(sin(p * 127.1) * 43758.5453); }

  vec3 lfHash31(float p) {
    return fract(sin(vec3(p * 127.1, p * 311.7, p * 74.7)) * 43758.5453);
  }

  float lfNoise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n = i.x + i.y * 57.0 + i.z * 113.0;
    float a = mix(lfHash11(n), lfHash11(n + 1.0), f.x);
    float b = mix(lfHash11(n + 57.0), lfHash11(n + 58.0), f.x);
    float c = mix(lfHash11(n + 113.0), lfHash11(n + 114.0), f.x);
    float d = mix(lfHash11(n + 170.0), lfHash11(n + 171.0), f.x);
    return mix(mix(a, b, f.y), mix(c, d, f.y), f.z);
  }

  float lfFbm(vec3 p) {
    float s = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      s += a * lfNoise(p);
      p *= 2.02;
      a *= 0.5;
    }
    return s;
  }

  // 8 - Mycelial Bloom: radial branch trunks with sub-filament whorls.
  // Bass drives reach, treble drives whorl spread, onsets flick only the tips.
  vec4 mycelialBloom(float t, vec2 seed) {
    float branches = 26.0 + floor(uFrequency * 8.0);
    float perBranch = max(1.0, floor(uCount / branches));
    float b = floor(aIndex / perBranch);
    float g = pow(clamp((mod(aIndex, perBranch) + 0.5) / perBranch, 0.0, 1.0), 0.8);

    vec3 r = lfHash31(b + 3.7);
    float ang = r.x * TAU;
    float tilt = (r.y - 0.5) * 2.3;
    vec3 dir = normalize(vec3(cos(ang) * cos(tilt), sin(tilt), sin(ang) * cos(tilt)));
    vec3 side = normalize(cross(dir, vec3(0.0, 1.0, 0.0) + 0.001));
    vec3 up = cross(dir, side);

    float reach = 19.0 + uBass * 9.0 + uBeat * 4.0;
    vec3 p = dir * g * reach;

    float sub = floor(seed.y * 6.0);
    float sa = sub / 6.0 * TAU + uTime * (0.3 + uFlow * 0.3) + b;
    float spread = pow(g, 1.7) * (1.3 + 4.0 * fract(seed.y * 6.0)) * (0.6 + uTreble * 1.2);
    p += side * cos(sa) * spread + up * sin(sa) * spread;

    float curl = sin(g * 5.0 + uTime * 0.6 + b * 2.1) * (1.4 + uMid * 2.0) * uAmplitude;
    p += up * curl * g + side * cos(g * 4.0 - uTime * 0.5) * curl * 0.6 * g;
    p += dir * uBeat * 5.0 * pow(g, 3.0);
    return vec4(p, g);
  }

  // 9 - Chladni Shell: standing-wave nodal lines on a sphere. Mode integers are
  // quantised so the pattern snaps between discrete states instead of sliding.
  vec4 chladniShell(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float ph = acos(clamp(y, -1.0, 1.0));
    float th = aIndex * GOLDEN_ANGLE + uTime * (0.02 + uRotation * 0.05);
    vec3 n = vec3(sin(ph) * cos(th), y, sin(ph) * sin(th));

    float m = floor(3.0 + uFrequency * 2.0 + uBass * 5.0);
    float k = floor(2.0 + uMid * 7.0 + uFrequency * 1.5);
    float w = sin(m * th) * sin(k * ph) - sin(k * th) * sin(m * ph);
    float node = 1.0 - smoothstep(0.0, 0.35, abs(w));

    float r = 20.5 + w * (2.0 + uTreble * 3.0) * uAmplitude + uBeat * 2.2;
    vec3 p = n * (r - node * 3.0);
    p += n * lfFbm(n * (3.0 + uFrequency) + uTime * 0.15) * 0.6;
    return vec4(p, node);
  }

  // 10 - Coral Skin: Gray-Scott flavoured membrane. Bass is the feed rate.
  // Single shell with symmetric ridges/pits so it never splits into two spheres.
  vec4 coralSkin(float t, vec2 seed) {
    float y = 1.0 - 2.0 * t;
    float ring = sqrt(max(0.0, 1.0 - y * y));
    float th = aIndex * GOLDEN_ANGLE + uTime * (0.015 + uRotation * 0.04);
    vec3 n = vec3(cos(th) * ring, y, sin(th) * ring);

    float feed = 0.9 + uBass * 1.5;
    float scale = 2.2 + uFrequency * 2.2;
    float drift = uTime * (0.12 + uFlow * 0.1);
    float a = lfFbm(n * scale + vec3(0.0, drift, 0.0));
    float b = lfFbm(n * scale * 2.03 - vec3(drift * 0.8, 0.0, drift * 0.4));
    float turing = sin((a - b) * 12.0 * feed + uTime * 0.4);
    float ridge = smoothstep(-0.15, 0.35, turing);

    float r = 20.5 + (ridge - 0.5) * (3.4 + uMid * 3.2) * uAmplitude;
    vec3 p = n * r;
    p += n * uBeat * ridge * 2.2;
    return vec4(p, ridge);
  }

  // 11 - Gyroid Lattice: Newton-project onto the gyroid minimal surface, then
  // breathe the cell size. The subtype channel is a hard radial cut here,
  // not a colour band.
  vec4 gyroidLattice(float t, vec2 seed) {
    vec3 p = (vec3(seed.x, t, seed.y) - 0.5) * 8.0;
    float cell = 0.85 + uFrequency * 0.3 + uBass * 0.3;
    float iso = sin(uTime * 0.25) * 0.3 + uMid * 0.5;
    for (int i = 0; i < 5; i++) {
      vec3 q = p * cell;
      float f = sin(q.x) * cos(q.y) + sin(q.y) * cos(q.z) + sin(q.z) * cos(q.x) - iso;
      vec3 g = vec3(
        cos(q.x) * cos(q.y) - sin(q.z) * sin(q.x),
        cos(q.y) * cos(q.z) - sin(q.x) * sin(q.y),
        cos(q.z) * cos(q.x) - sin(q.y) * sin(q.z)) * cell;
      p -= f * g / max(dot(g, g), 0.001);
    }
    float fade = 1.0 - smoothstep(2.7, 3.8, length(p));
    p.xz = rot((uBeat * 0.35 + uTime * 0.05) * p.y) * p.xz;
    p *= 5.8;
    return vec4(p, fade);
  }

  // 12 - Torus Knot Weave: (p,q) knot swept as a tube. p and q lerp to new
  // random integers each downbeat, so the topology visibly re-ties itself.
  vec4 torusKnotWeave(float t, vec2 seed) {
    float beatIdx = floor(uTime * 0.5);
    float pA = 2.0 + floor(lfHash11(beatIdx) * 5.0);
    float qA = 3.0 + floor(lfHash11(beatIdx + 9.0) * 6.0);
    float pB = 2.0 + floor(lfHash11(beatIdx + 1.0) * 5.0);
    float qB = 3.0 + floor(lfHash11(beatIdx + 10.0) * 6.0);
    float k = smoothstep(0.0, 1.0, fract(uTime * 0.5));
    float pp = mix(pA, pB, k);
    float qq = mix(qA, qB, k);

    float strands = 3.0 + floor(uFrequency * 2.0);
    float strand = floor(seed.x * strands);
    float a = t * TAU + strand * 0.7;

    float rr = 10.0 + 3.8 * cos(qq * a) + uBass * 2.5;
    vec3 c = vec3(rr * cos(pp * a), -3.8 * sin(qq * a), rr * sin(pp * a));
    float a2 = a + 0.01;
    float rr2 = 10.0 + 3.8 * cos(qq * a2) + uBass * 2.5;
    vec3 c2 = vec3(rr2 * cos(pp * a2), -3.8 * sin(qq * a2), rr2 * sin(pp * a2));
    vec3 tangent = normalize(c2 - c);
    vec3 nrm = normalize(cross(tangent, vec3(0.0, 1.0, 0.0) + 0.001));
    vec3 bin = cross(tangent, nrm);

    float tubeA = seed.y * TAU + uTime * 0.8 + strand;
    float rad = (0.8 + uTreble * 1.3) * (0.7 + 0.5 * sin(a * 6.0 + uTime)) * (0.6 + uAmplitude);
    vec3 p = (c + (nrm * cos(tubeA) + bin * sin(tubeA)) * rad) * 1.5;
    p += tangent * uBeat * 3.0;
    return vec4(p, 0.5 + 0.5 * sin(qq * a));
  }

  // 13 - Cymatic Bell: a Rayleigh water bell. Sheet leaves the nozzle, flares
  // and closes; ripple rings ride outward and the rim beads on transients.
  vec4 cymaticBell(float t, vec2 seed) {
    float run = pow(t, 0.85);
    float flare = 0.35 + uBass * 0.9;
    float radius = sin(run * PI) * (14.0 * flare + 5.0) + 0.3;
    float drop = -11.0 + run * 22.0 - pow(run, 2.2) * 6.5;
    float th = seed.x * TAU + uTime * (0.02 + uRotation * 0.05);

    vec3 p = vec3(radius * cos(th), drop, radius * sin(th));
    float rings = sin(run * (18.0 + uFrequency * 12.0) - uTime * 3.0 - uBeat * 6.0);
    p.y += rings * (0.6 + uMid * 3.0) * uAmplitude * run;
    p.xz *= 1.0 + rings * 0.05 * (0.3 + uTreble);
    p += normalize(vec3(p.x, 2.0, p.z)) * uBeat * pow(run, 3.0) * 5.0;
    return vec4(p, 0.5 + 0.5 * rings);
  }

  // 14 - Voronoi Shatter: shell clustered into cells around random sites. Each
  // transient throws the cells outward with per-cell spin and stagger.
  vec4 voronoiShatter(float t, vec2 seed) {
    float cells = 30.0 + floor(uFrequency * 20.0);
    float ci = floor(t * cells);
    vec3 site = normalize(lfHash31(ci * 1.7) - 0.5 + 0.001);
    vec3 local = (lfHash31(aIndex * 0.013 + ci) - 0.5) * (4.0 + uFrequency * 2.0);

    float shell = 18.5 + uBass * 3.5;
    float burst = uBeat * (0.4 + lfHash11(ci + 2.0));
    vec3 p = site * shell + local + site * burst * 16.0;

    float spin = burst * 3.0 + uTime * 0.15;
    vec3 rel = p - site * shell;
    rel.xy = rot(spin) * rel.xy;
    rel.yz = rot(spin) * rel.yz;
    p = site * shell + rel;
    p += site * sin(uTime * 0.8 + ci) * (0.6 + uMid * 1.4);
    return vec4(p, 0.45 + lfHash11(ci + 5.0) * 0.55);
  }

  // 15 - Klein Ribbon: figure-8 immersion of the Klein bottle - a ribbon that
  // passes through itself, with a travelling band riding the surface.
  vec4 kleinRibbon(float t, vec2 seed) {
    float a = t * TAU;
    float b = seed.x * TAU;
    float R = 13.0 + uBass * 4.0;
    float w = 3.6 + uMid * 3.0 + sin(uTime * 0.4);
    float halfA = a * 0.5;
    float sb = sin(b);
    float f = R + w * (cos(halfA) * sb - sin(halfA) * sin(2.0 * b));

    vec3 p = vec3(
      f * cos(a),
      f * sin(a),
      w * (sin(halfA) * sb + cos(halfA) * sin(2.0 * b)));
    float bandWave = sin(a * (3.0 + uFrequency * 3.0) - uTime * 1.4);
    p += normalize(vec3(p.x, p.y, 0.001)) * bandWave * (0.8 + uTreble * 2.2) * uAmplitude;
    p.z += sin(b * 3.0 + uTime) * 0.8;
    p += normalize(p + 0.001) * uBeat * 2.4;
    p = rotateX(p, -1.1);
    return vec4(p, 0.5 + 0.5 * bandWave);
  }

  // 16 - Ferrofluid Crown: the Rosensweig normal-field instability. A mirror
  // pool climbs into a hexagonal spike lattice as the low end rises; onsets
  // throw a crown of beads off the tips and ring a capillary wave outward.
  vec4 ferrofluidCrown(float t, vec2 seed) {
    vec2 hexA = vec2(1.0, 0.0);
    vec2 hexB = vec2(-0.5, 0.8660254);
    vec2 hexC = vec2(-0.5, -0.8660254);

    float pool = 25.0;
    float ang = seed.x * TAU;
    vec2 q = vec2(cos(ang), sin(ang)) * sqrt(t) * pool;

    float cell = 1.05 + uFrequency * 0.22;
    vec2 drift = vec2(uTime * 0.16, uTime * 0.11);
    // Three quarters of the sheet climbs the field gradient into spikes; the
    // rest stays behind as the flat mirror the spikes need for contrast. The
    // climb stops short of the summit so each spike keeps a conical skirt.
    float climb = step(0.28, seed.y);
    float stepLen = 1.5 / cell;
    for (int i = 0; i < 2; i++) {
      vec2 v = q * cell + drift;
      vec2 g = -(sin(dot(v, hexA)) * hexA + sin(dot(v, hexB)) * hexB + sin(dot(v, hexC)) * hexC);
      q += g / (length(g) + 0.35) * stepLen * climb;
      stepLen *= 0.42;
    }

    vec2 w = q * cell + drift;
    float field = (cos(dot(w, hexA)) + cos(dot(w, hexB)) + cos(dot(w, hexC))) / 3.0;
    float peak = clamp((field + 0.05) / 1.05, 0.0, 1.0);

    float rr = length(q);
    float taper = 1.0 - smoothstep(0.58, 1.02, rr / pool);
    float amp = (2.4 + uBass * 15.0 + uBeat * 4.5) * uAmplitude;
    float spike = pow(peak, 3.6) * amp * taper;
    float ripple = sin(rr * 0.5 - uTime * 3.0) * exp(-rr * 0.055) *
      (0.4 + uBeat * 3.2) * uAmplitude;
    float chop = sin(q.x * 0.85 + uTime * 1.6) * sin(q.y * 0.79 - uTime * 1.2) *
      (0.16 + uTreble * 1.05);
    float bead = climb * pow(peak, 9.0) * uBeat *
      (0.5 + lfHash11(aIndex * 0.0313)) * 6.5;
    float bowl = rr * rr * -0.006;

    vec3 p = vec3(q.x, spike + ripple + chop + bead + bowl - 4.0, q.y);
    float lit = clamp(pow(peak, 2.4) * (0.22 + climb * 0.78) + abs(ripple) * 0.10, 0.0, 1.0);
    return vec4(p, lit);
  }

  // 17 - Magnetic Curtain: charged matter bound to dipole L-shells. Bass
  // compresses the sunward side and drags the tail, mids fold the sheets,
  // treble sends beads down the lines and onsets fire a substorm front.
  // 17 - Magnetic Curtain: standing auroral sheets on the oval. Each sheet is
  // a meandering ground curve extruded up the field, striated into rays. Bass
  // raises and leans the curtain, mids drive the fold count and depth, treble
  // sharpens the rays, and each onset drives a surge along the sheet.
  vec4 magneticCurtain(float t, vec2 seed) {
    float sheets = 5.0 + floor(uFrequency * 3.0);
    float ci = floor(seed.x * sheets);
    float v = fract(seed.x * sheets);
    // Bias particles low so the bright lower border stays dense.
    float h = pow(t, 1.4);

    float oval = 19.0 + lfHash11(ci * 3.1) * 9.0;
    float base = lfHash11(ci * 7.7) * TAU + uTime * (0.03 + uRotation * 0.06);
    float span = 1.0 + lfHash11(ci * 5.3) * 1.1;
    float a = base + (v - 0.5) * span;

    float folds = 3.0 + floor(uMid * 5.0);
    float meander = sin(v * folds * PI + uTime * (0.5 + uFlow * 0.45) + ci * 2.3) *
      (1.2 + uMid * 4.5) * uAmplitude;
    float radius = oval + meander + h * (1.6 + uBass * 3.4);

    float top = 17.0 + uBass * 11.0 + uBeat * 3.0;
    // Real curtains have an undulating lower border, not a flat hem.
    float hem = sin(v * folds * PI * 0.5 + ci * 1.7 + uTime * 0.3) * (0.8 + uMid * 2.4);
    vec3 p = vec3(cos(a) * radius, -9.5 + hem + h * top, sin(a) * radius);

    // Sheets stay thin so they read as surfaces, not clouds.
    p.xz += vec2(cos(a), sin(a)) * (seed.y - 0.5) * (0.25 + uMid * 0.5) * (0.4 + h);
    p.y += (fract(seed.y * 11.3) - 0.5) * 0.7;

    float surgeAt = fract(uTime * 0.4 + ci * 0.31);
    float surge = uBeat * (1.0 - smoothstep(0.0, 0.22, abs(v - surgeAt)));
    p.y += surge * 4.5 * h;

    // Field-aligned rays: fine vertical filaments across the sheet.
    float rayPhase = fract(v * (60.0 + floor(uTreble * 60.0)));
    float ray = pow(1.0 - abs(2.0 * rayPhase - 1.0), 3.0);
    float lower = pow(1.0 - h, 1.6);
    float glow = lower * (0.55 + uBeat * 0.35) +
      ray * (0.18 + uTreble * 0.55) * (0.35 + lower) +
      surge * 0.5;
    return vec4(p, clamp(0.10 + glow, 0.0, 1.0));
  }

  // 18 - Fractal Flame: a three-map iterated function system with a swirl
  // variation. The contraction ratio is deliberately high (0.76) so the
  // sub-copies overlap into continuous filaments; a strongly contractive
  // gasket would break into visible clumps at these particle counts. Each
  // particle walks its own fixed map sequence, so the attractor deforms
  // smoothly instead of popping, and colour is the running transform
  // coordinate that flame renderers use.
  vec4 fractalFlame(float t, vec2 seed) {
    vec3 p = (lfHash31(aIndex * 0.01731 + 5.17) - 0.5) * 2.0;
    // Keep the hash argument small; float sin() aliases badly at high indices
    // and correlated map choices collapse the attractor into a few clumps.
    float rng = fract(aIndex * 0.6180339887 + seed.x * 0.3247179572 + seed.y * 0.7548776662);
    float spin = uTime * (0.05 + uRotation * 0.12);
    float sphereAmt = 0.06 + uBass * 0.14;
    float sinAmt = 0.04 + uTreble * 0.14;
    float swirlAmt = 0.06 + uMid * 0.30;
    float scale = 0.80;
    float reach = 0.60 + uBass * 0.10 + uBeat * 0.05;
    float colorCoord = 0.5;

    for (int i = 0; i < 22; i++) {
      float c = floor(fract(sin(rng * 127.1 + float(i) * 311.7 + 4.7) * 43758.5453) * 3.0);
      vec3 shift = vec3(1.0, 0.0, 0.0);
      float ang = spin;
      if (c > 1.5) {
        shift = vec3(-0.5, -0.87, -0.3);
        ang = spin * 0.8 + 4.1888;
      } else if (c > 0.5) {
        shift = vec3(-0.5, 0.87, 0.3);
        ang = -spin * 1.2 + 2.0944;
      }

      vec3 q = p;
      q.xy = rot(ang) * q.xy;
      q.yz = rot(ang * 0.45 + c * 0.9) * q.yz;
      q = q * scale + shift * reach;

      float r2 = max(dot(q, q), 0.05);
      vec3 swirl = q;
      swirl.xy = rot(r2 * swirlAmt) * swirl.xy;
      vec3 v = mix(q, swirl, 0.8);
      v = mix(v, q / r2, sphereAmt);
      v = mix(v, sin(q * (2.4 + uFrequency * 1.1)) * 0.8, sinAmt);
      p = clamp(v, vec3(-3.0), vec3(3.0));
      colorCoord = mix(colorCoord, c * 0.5, 0.35);
    }

    vec3 flame = p * (8.5 + uBass * 1.8) * (0.8 + uAmplitude * 0.5);
    flame += normalize(flame + 0.001) * uBeat * 2.4;
    return vec4(flame, clamp(colorCoord, 0.0, 1.0));
  }

  // 19 - Vortex Cascade: a nozzle sheds smoke rings that roll poloidally as
  // they travel, widen as they age, and die before the next is born - so the
  // pulse visibly leaves the body of the form.
  vec4 vortexCascade(float t, vec2 seed) {
    float rings = 6.0;
    float ri = floor(seed.x * rings);
    float speed = 0.10 + uFlow * 0.075 + uBass * 0.03;
    float age = fract(uTime * speed + ri / rings + lfHash11(ri + 4.3) * 0.04);

    float toroidal = aIndex * GOLDEN_ANGLE;
    float poloidal = seed.y * TAU + age * TAU * (2.2 + uMid * 3.4);

    float tube = (2.6 - age * 1.1) * (0.55 + uAmplitude * 0.9);
    float core = 4.2 + age * (17.0 + uBass * 9.0);
    // Widnall azimuthal instability: the rim scallops as treble rises.
    core += sin(toroidal * (4.0 + floor(uTreble * 5.0)) + age * 7.0) *
      tube * (0.18 + uTreble * 0.55) * age;

    vec2 dir = vec2(cos(toroidal), sin(toroidal));
    float radial = core + cos(poloidal) * tube;
    float travel = age * (30.0 + uBass * 10.0) - 14.0;
    vec3 p = vec3(dir.x * radial, travel + sin(poloidal) * tube, dir.y * radial);

    float young = 1.0 - smoothstep(0.0, 0.30, age);
    p += vec3(dir.x, 0.35, dir.y) * uBeat * young * 5.0;

    float env = smoothstep(0.0, 0.12, age) * (1.0 - smoothstep(0.70, 1.0, age));
    float sub = env * (0.42 + 0.58 * (0.5 + 0.5 * cos(poloidal - 1.2)));

    // A slow feeding column keeps the nozzle alive between rings.
    float column = step(0.88, fract(seed.x * rings));
    float cy = (fract(seed.y * 7.31) - 0.5) * 15.0;
    float ca = seed.y * TAU * 5.0 + uTime * 2.0 + cy * 0.4;
    float cr = (1.5 + uBass * 2.4) * (0.35 + 0.65 * exp(-abs(cy) * 0.12));
    p = mix(p, vec3(cos(ca) * cr, cy * 0.7 - 9.0, sin(ca) * cr), column);
    sub = mix(sub, 0.26 + 0.24 * (0.5 + 0.5 * sin(ca * 2.0)), column);
    return vec4(p, clamp(sub, 0.0, 1.0));
  }

  // Divergence-free ABC flow. Analytic and cheap, and chaotic enough to read
  // as turbulence without sampling noise derivatives for a real curl.
  vec3 lfAbcFlow(vec3 q, float a, float b, float c) {
    return vec3(
      a * sin(q.z) + c * cos(q.y),
      b * sin(q.x) + a * cos(q.z),
      c * sin(q.y) + b * cos(q.x));
  }

  // 20 - Smoke Column: a buoyant plume advected through two octaves of that
  // flow. Every particle integrates its own streamline out of the source, so
  // the column widens and curls the further it has already risen. Bass is
  // thrust, mids are the swirl gain, treble is the fine second octave.
  vec4 smokeColumn(float t, vec2 seed) {
    // Bias toward older particles; plume volume grows with height, so a
    // uniform age spread stacks every particle into a solid core at the source.
    float age = pow(t, 0.55);
    float sa = seed.x * TAU;
    float sr = sqrt(seed.y) * (2.2 + uBass * 1.8);
    vec3 p = vec3(cos(sa) * sr, -17.0, sin(sa) * sr);

    float rise = 26.0 + uBass * 14.0;
    float swirl = 7.0 + uMid * 14.0;
    float drift = uTime * (0.25 + uFlow * 0.3);
    float dt = age / 6.0;
    for (int i = 0; i < 6; i++) {
      float u = (float(i) + 0.5) / 6.0;
      vec3 q1 = p * 0.16 + vec3(0.0, -drift, drift * 0.35);
      vec3 q2 = p * 0.42 - vec3(drift * 0.6, drift * 0.4, 0.0);
      vec3 v = lfAbcFlow(q1, 1.0, 0.8, 0.65) * swirl +
        lfAbcFlow(q2, 0.7, 0.9, 0.5) * swirl * (0.35 + uTreble * 0.55);
      v.xz *= 0.2 + u * age * 2.2;
      // Buoyancy bleeds off with height and the cap rolls outward. Entrainment
      // pulls back toward the axis so bulk flow drift cannot walk the plume
      // off frame while the orbit camera is moving.
      v.y += rise * (1.0 - u * 0.45);
      v.xz += p.xz / (length(p.xz) + 0.5) * rise * 0.14 * pow(u * age, 2.0);
      v.xz -= p.xz * 1.1;
      p += v * dt;
    }

    p += normalize(vec3(p.x, 0.6, p.z)) * uBeat * age * 5.5 * uAmplitude;
    p.y += uBeat * 2.0;

    float body = pow(1.0 - age, 0.85);
    float curl = 0.5 + 0.5 * sin(p.x * 0.22 + p.z * 0.19 + uTime * 0.8);
    return vec4(p, clamp(0.05 + body * (0.42 + curl * 0.42), 0.0, 1.0));
  }

  float lfMusicalRatio(float h) {
    float idx = floor(h * 5.0);
    if (idx < 0.5) return 1.0;
    if (idx < 1.5) return 1.5;
    if (idx < 2.5) return 1.3333333;
    if (idx < 3.5) return 1.25;
    return 2.0;
  }

  // 21 - Harmonograph: damped pendulums drawing a Lissajous rosette. The
  // frequency pair re-locks to a new musical interval on a slow clock, and
  // loudness lifts the damping so the figure opens instead of spiralling shut.
  vec4 harmonograph(float t, vec2 seed) {
    // Few strands and few traversals: the figure has to read as a drawn line,
    // and dozens of overlapping passes just average into dust.
    float strands = 1.0 + floor(uFrequency * 1.5);
    float band = seed.x * strands;
    float si = floor(band);
    float u = t * (26.0 + floor(uFrequency * 14.0));

    float slot = floor(uTime * 0.22);
    float k = smoothstep(0.0, 1.0, fract(uTime * 0.22));
    float ratio = mix(
      lfMusicalRatio(lfHash11(slot * 1.7)),
      lfMusicalRatio(lfHash11(slot * 1.7 + 1.0)), k);

    float energy = clamp((uBass + uMid + uTreble) * 0.42, 0.0, 1.0);
    float env = exp(-u * (0.008 + (1.0 - energy) * 0.030));
    env += uBeat * 0.18 * (1.0 - env);

    // A narrow phase spread across the strand turns the curve into a ribbon.
    float ph = si * 2.1 + (fract(band) - 0.5) * 0.06;
    float f1 = 2.0;
    float f2 = f1 * ratio;
    float f3 = 3.0;
    float f4 = f3 * ratio;
    float f5 = 2.5;

    // All three axes carry a damped pair. A planar figure would only read
    // face-on, and the orbit camera does not stay there.
    vec3 p = vec3(
      sin(f1 * u + ph) + sin(f2 * u + 1.7 + ph * 0.5) * 0.78,
      sin(f3 * u + 0.9 + ph) + sin(f4 * u + 2.4) * 0.72,
      sin(f5 * u + 1.2 + ph) * 0.9 + sin(f5 * ratio * u + 0.4) * 0.55);
    p *= env * (11.5 + uBass * 4.0) * (0.7 + uAmplitude * 0.6);
    p += normalize(p + 0.001) * sin(u * 3.0 + uTime) * uTreble * 0.6;

    return vec4(p, clamp(0.25 + (env - 0.20) * 0.95, 0.0, 1.0));
  }

  vec4 sampleMathMode(float mode, float t, vec2 seed) {
    float m = mod(mode, 22.0);
    if (m < 0.5) {
      return radialPulse(t, seed);
    } else if (m < 1.5) {
      return superform(t, seed);
    } else if (m < 2.5) {
      return spiralVortex(t, seed);
    } else if (m < 3.5) {
      return hopfFibration(t, seed);
    } else if (m < 4.5) {
      return fibonacciSequence(t, seed);
    } else if (m < 5.5) {
      return plasmaMembrane(t, seed);
    } else if (m < 6.5) {
      return noiseBlossom(t, seed);
    } else if (m < 7.5) {
      return livingShell(t, seed);
    } else if (m < 8.5) {
      return mycelialBloom(t, seed);
    } else if (m < 9.5) {
      return chladniShell(t, seed);
    } else if (m < 10.5) {
      return coralSkin(t, seed);
    } else if (m < 11.5) {
      return gyroidLattice(t, seed);
    } else if (m < 12.5) {
      return torusKnotWeave(t, seed);
    } else if (m < 13.5) {
      return cymaticBell(t, seed);
    } else if (m < 14.5) {
      return voronoiShatter(t, seed);
    } else if (m < 15.5) {
      return kleinRibbon(t, seed);
    } else if (m < 16.5) {
      return ferrofluidCrown(t, seed);
    } else if (m < 17.5) {
      return magneticCurtain(t, seed);
    } else if (m < 18.5) {
      return fractalFlame(t, seed);
    } else if (m < 19.5) {
      return vortexCascade(t, seed);
    } else if (m < 20.5) {
      return smokeColumn(t, seed);
    }
    return harmonograph(t, seed);
  }

  void main() {
    float count = max(1.0, uCount);
    float t = clamp((aIndex + 0.5) / count, 0.0, 1.0);

    vec4 packed = sampleMathMode(uMode, t, aSeed);
    vec3 p = packed.xyz;
    float subtype = packed.w;

    float preEnergy = clamp((uBass + uMid + uTreble) / 3.0, 0.0, 1.2);
    float livingOptics = 0.0;
    if ((uMode >= 0.5 && uMode < 1.5) || uMode >= 4.5) livingOptics = 1.0;
    float twinkle = sin(aIndex * 0.071 + uTime * 3.4 + aSeed.y * TAU) * 0.5 + 0.5;
    float sparkle = livingOptics * pow(twinkle, 6.0) * (0.66 + uTreble * 0.42);

    vec3 n = normalize(p + vec3(0.0001));
    float micro = sin(uTime * 1.1 + aSeed.x * 41.0 + t * 23.0) * 0.18
                + sin(uTime * 0.7 + aSeed.y * 17.0 + length(p) * 0.08) * 0.12;
    p += n * micro * uAmplitude * (0.72 + uTreble * 0.35);

    p = rotateY(p, uTime * (0.030 + uRotation * 0.13));
    p = rotateX(p, sin(uTime * 0.16) * 0.08 * uDepthScale);
    p.x *= uOutputSpread;
    p.y *= uOutputSpreadY;

    vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);

    float sizePulse = smoothstep(0.45, 0.95, preEnergy) * 0.06 + uBeat * 0.04;
    float distScale = mix(1.0, 150.0 / max(46.0, -mvPosition.z), 0.36);
    float modePointScale = 1.0;
    if (uMode >= 2.5 && uMode < 3.5) modePointScale = 0.88;
    else if (uMode >= 3.5 && uMode < 4.5) modePointScale = 0.66;
    else if (uMode >= 4.5 && uMode < 5.5) modePointScale = 0.72;
    else if (uMode >= 5.5) modePointScale = 0.84;
#ifdef USE_INSTANCED_BILLBOARD
    float billboardSize = uPointSize * 0.16 *
      (0.92 + uIntensity * 0.18 + sizePulse * 0.55) *
      (1.0 + sparkle * 0.72) * modePointScale;
    mvPosition.xy += position.xy * billboardSize;
    vSpriteUv = uv;
#else
    float minPointSize = uMode >= 2.5 ? 2.1 : 1.6;
    gl_PointSize = max(
      minPointSize,
      uPointSize * (0.92 + uIntensity * 0.18 + sizePulse * 0.55) *
        (1.0 + sparkle * 0.72) * distScale * modePointScale
    );
#endif
    gl_Position = projectionMatrix * mvPosition;

    vec3 normal = normalize(p + vec3(0.0001));
    vec3 lightDir = normalize(vec3(0.55, 0.62, 0.55));
    float light = 0.55 + max(0.0, dot(normal, lightDir)) * 0.55;
    float rim = pow(1.0 - abs(dot(normalize(-mvPosition.xyz), normal)), 2.2);
    float grad = clamp(0.45 + p.y * 0.012 + length(p.xz) * 0.011, 0.0, 1.0);

    float warmthBias = 0.0;
    float modeVis = 1.0;
    if (uMode < 0.5) {
      warmthBias = 0.05 + subtype * 0.15;
      grad += subtype * 0.25;
    } else if (uMode < 1.5) {
      warmthBias = 0.08;
    } else if (uMode < 2.5) {
      warmthBias = subtype > 0.5 ? 0.30 : -0.45;
      grad = subtype > 0.5 ? 0.65 : 0.30;
    } else if (uMode < 3.5) {
      modeVis = clamp(0.34 + subtype * 0.82, 0.0, 1.0);
      warmthBias = 0.12 + subtype * 0.12;
      grad = 0.34 + subtype * 0.38 + aSeed.x * 0.10;
      rim *= 0.70 + 0.30 * modeVis;
      light *= 0.72 + 0.34 * modeVis;
    } else if (uMode < 4.5) {
      modeVis = 0.40 + subtype * 0.58;
      warmthBias = 0.22 + subtype * 0.16;
      grad = 0.32 + subtype * 0.55 + aSeed.y * 0.08;
      rim *= 0.85 + subtype * 0.25;
    } else if (uMode < 5.5) {
      modeVis = 0.58 + subtype * 0.42;
      warmthBias = -0.34 + subtype * 0.12;
      grad = 0.28 + subtype * 0.62 + aSeed.x * 0.06;
      rim *= 0.62 + subtype * 0.28;
      light *= 0.78 + subtype * 0.25;
    } else if (uMode < 6.5) {
      modeVis = 0.72 + subtype * 0.28;
      warmthBias = 0.08 + subtype * 0.16;
      grad = 0.30 + subtype * 0.62;
      rim *= 0.82 + subtype * 0.24;
    } else if (uMode < 7.5) {
      modeVis = 0.78 + subtype * 0.22;
      warmthBias = -0.04 + subtype * 0.12;
      grad = 0.34 + subtype * 0.48 + aSeed.y * 0.08;
      rim *= 0.88 + subtype * 0.18;
    } else {
      // Living Forms cores. The subtype channel is the structural band for
      // every core except Gyroid Lattice, where it is a hard radial cut, and
      // Vortex Cascade, where it is the birth/death envelope of each ring.
      modeVis = clamp(0.34 + subtype * 0.78, 0.0, 1.12);
      if (uMode >= 10.5 && uMode < 11.5) modeVis = subtype * subtype;
      // Fractal Flame is the one volumetric core; thin it so overlapping
      // sheets do not stack into a solid additive blob.
      if (uMode >= 17.5 && uMode < 18.5) modeVis *= 0.62;
      if (uMode >= 18.5 && uMode < 19.5) modeVis = clamp(subtype * 1.08, 0.0, 1.12);
      // Smoke has to dissolve completely at the top of the plume, so its
      // visibility comes straight from the density channel.
      if (uMode >= 19.5 && uMode < 20.5) modeVis = clamp(subtype * 1.15, 0.0, 1.12);
      warmthBias = -0.10 + subtype * 0.26;
      grad = 0.28 + subtype * 0.56 + aSeed.x * 0.08;
      rim *= 0.80 + subtype * 0.30;
      light *= 0.80 + subtype * 0.28;
    }

    vec3 pal = referencePalette(clamp(grad, 0.0, 1.0), preEnergy, warmthBias);
    vec3 emberLow = hsl2rgb(vec3(fract(uHue - 0.02 * uColorSpread), 0.97 * uSaturation, 0.20));
    vec3 emberMid = hsl2rgb(vec3(fract(uHue + 0.04 * uColorSpread), 0.96 * uSaturation, 0.43));
    vec3 emberHot = hsl2rgb(vec3(fract(uHue + 0.11 * uColorSpread), 0.94 * uSaturation, 0.64)) * 1.18;
    vec3 ember = mix(emberLow, emberMid, smoothstep(0.16, 0.72, grad));
    float hotMix = clamp(smoothstep(0.82, 1.0, grad) * 0.38 + sparkle * 0.56, 0.0, 0.82);
    ember = mix(ember, emberHot, hotMix);
    float emberMix = livingOptics * smoothstep(0.24, 0.76, uWarmth) * (1.0 - uColorSpread * 0.72);
    pal = mix(pal, ember, emberMix);
    float audioTravel = uTime * (0.035 + preEnergy * 0.16 + uBeat * 0.08);
    float bassWave = sin(length(p.xz) * 0.095 - uTime * (0.22 + uBass * 0.46)) *
      (0.025 + uBass * 0.065);
    float midWave = sin(p.y * 0.072 + atan(p.z, p.x) * 2.0 - uTime * (0.18 + uMid * 0.38)) *
      (0.02 + uMid * 0.055);
    float colorTravel = uColorFlow * (audioTravel + bassWave + midWave);
    float spectrumPhase = fract(
      atan(p.z, p.x) / TAU + 0.5 + p.y * 0.006 + aSeed.x * 0.04 + colorTravel
    );
    vec3 spectrum = hsl2rgb(vec3(
      fract(uHue + (spectrumPhase - 0.5) * uColorSpread),
      0.98 * uSaturation,
      0.4
    ));
    float spectrumMix = livingOptics * smoothstep(0.04, 0.52, uColorSpread);
    pal = mix(pal, spectrum, spectrumMix);
    vec3 hueTint = hsl2rgb(vec3(fract(uHue + 0.0), 0.45, 0.55));
    pal = mix(pal, hueTint, 0.05 * (1.0 - emberMix));

    float modeBrightness = uMode < 3.5 ? 1.0 : 0.90;
    if (uMode >= 2.5 && uMode < 3.5) modeBrightness = 0.92;
    float standardGain = 0.94 + light * 0.44 + rim * 0.28 + preEnergy * 0.10;
    float livingDepth = 0.42 + light * 0.34 + rim * 0.46;
    float livingContour = mix(0.68, 1.26, smoothstep(0.16, 0.9, grad));
    float livingGain = (livingDepth * livingContour + preEnergy * 0.07) * 1.5;
    vColor = pal * mix(standardGain, livingGain, livingOptics) *
      modeBrightness * (1.0 + sparkle * mix(0.48, 0.4, livingOptics));
    float modeAlphaScale = 1.0;
    if (uMode >= 2.5 && uMode < 3.5) modeAlphaScale = 0.84;
    else if (uMode >= 3.5) modeAlphaScale = mix(0.84, 0.94, livingOptics);
    vAlpha = (0.68 + uIntensity * 0.08 + preEnergy * 0.025) * modeVis *
      modeAlphaScale * uOpacity * (1.0 + sparkle * 0.22);
    vEnergy = preEnergy;
    vSparkle = sparkle;
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vEnergy;
  varying float vSparkle;
#ifdef USE_INSTANCED_BILLBOARD
  varying vec2 vSpriteUv;
#endif

  void main() {
#ifdef USE_INSTANCED_BILLBOARD
    vec2 uv = vSpriteUv - 0.5;
#else
    vec2 uv = gl_PointCoord - 0.5;
#endif
    float d = length(uv);
    if (d > 0.5) discard;

    float core = smoothstep(0.5, 0.0, d);
    core = pow(core, 2.2);
    float halo = exp(-d * d * 9.0) * (0.14 + vEnergy * 0.04);
    float pinLight = exp(-d * d * 32.0) * vSparkle * 0.5;

    float alpha = clamp((core + halo + pinLight) * vAlpha * 1.05, 0.0, 1.0);
    if (alpha < 0.01) discard;

    vec3 col = vColor * (0.94 + core * 0.74 + halo * 0.38 + vEnergy * 0.10);
    col += normalize(vColor + vec3(0.01)) * pinLight * 2.15;
    col = min(col, vec3(2.35));
    gl_FragColor = vec4(col, alpha);
  }
`;

export class LiveMathParticleScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 3, g: 4, b: 13, alpha: 0.48, blurPx: 58 };
  readonly cameraPersonality: CameraPersonality;
  beatLevel = 0;

  private readonly geometry: THREE.BufferGeometry;
  private readonly billboardGeometry: THREE.InstancedBufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly particles: THREE.Points | THREE.Mesh;
  private readonly usesLivingFormOptics: boolean;
  private readonly uniforms: UniformMap = {
    uTime: { value: 0 },
    uCount: { value: 40_000 },
    uMode: { value: 0 },
    uHue: { value: 0 },
    uSaturation: { value: 1 },
    uOpacity: { value: 1 },
    uIntensity: { value: 1 },
    uPointSize: { value: 1 },
    uAmplitude: { value: 0.3 },
    uFrequency: { value: 2 },
    uRotation: { value: 0.55 },
    uDepthScale: { value: 0.4 },
    uWarmth: { value: 0.62 },
    uColorSpread: { value: 0.28 },
    uColorFlow: { value: 0.55 },
    uOutputSpread: { value: 1 },
    uOutputSpreadY: { value: 1 },
    uBass: { value: 0 },
    uMid: { value: 0 },
    uTreble: { value: 0 },
    uBeat: { value: 0 },
    uFlow: { value: 1 },
  };
  private time = 0;
  private visualTime: number | null = null;

  constructor(width: number, height: number, mode: LiveMathParticleMode) {
    this.usesLivingFormOptics = LIVING_FORM_MODES.has(mode);
    this.cameraPersonality = "orbit";
    this.camera = new THREE.PerspectiveCamera(48, width / Math.max(1, height), 0.1, 600);
    if (mode === "superform") {
      this.camera.position.set(0, 14, 90);
    } else if (mode === "spiralVortex") {
      this.camera.position.set(0, 28, 55);
    } else if (mode === "fibonacciSequence") {
      this.camera.position.set(0, 34, 74);
    } else if (mode === "plasmaMembrane") {
      this.camera.position.set(0, 18, 68);
    } else if (mode === "cymaticBell") {
      this.camera.position.set(0, 10, 62);
    } else if (mode === "ferrofluidCrown") {
      this.camera.position.set(0, 24, 62);
    } else if (mode === "magneticCurtain") {
      this.camera.position.set(0, 5, 74);
    } else if (mode === "fractalFlame") {
      this.camera.position.set(0, 6, 56);
    } else if (mode === "vortexCascade") {
      this.camera.position.set(0, 8, 74);
    } else if (mode === "smokeColumn") {
      this.camera.position.set(0, 2, 72);
    } else if (mode === "harmonograph") {
      this.camera.position.set(0, 6, 62);
    } else if (mode === "gyroidLattice" || mode === "torusKnotWeave" || mode === "kleinRibbon") {
      this.camera.position.set(0, 14, 66);
    } else {
      this.camera.position.set(0, 18, 72);
    }
    this.camera.lookAt(0, 0, 0);

    const indices = new Float32Array(MAX_PARTICLES);
    const seeds = new Float32Array(MAX_PARTICLES * 2);
    for (let index = 0; index < MAX_PARTICLES; index++) {
      indices[index] = index;
      seeds[index * 2] = hashUnit(index + 13);
      seeds[index * 2 + 1] = hashUnit(index + 29);
    }

    const plane = new THREE.PlaneGeometry(1, 1);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.index = plane.index?.clone() ?? null;
    geometry.setAttribute("position", plane.getAttribute("position").clone());
    geometry.setAttribute("uv", plane.getAttribute("uv").clone());
    geometry.setAttribute("aIndex", new THREE.InstancedBufferAttribute(indices, 1));
    geometry.setAttribute("aSeed", new THREE.InstancedBufferAttribute(seeds, 2));
    geometry.instanceCount = this.uniforms.uCount.value;
    plane.dispose();
    this.geometry = geometry;
    this.billboardGeometry = geometry;
    this.uniforms.uMode.value = MODE_INDEX[mode];

    this.material = new THREE.ShaderMaterial({
      defines: { USE_INSTANCED_BILLBOARD: 1 },
      uniforms: this.uniforms,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.particles = new THREE.Mesh(this.geometry, this.material);
    this.particles.frustumCulled = false;
    this.scene.add(this.particles);
    this.setSize(width, height);
  }

  setSize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  setVisualTime(timeSeconds: number | null): void {
    this.visualTime = timeSeconds;
  }

  setOutputSpread(spreadX: number, spreadY: number): void {
    this.uniforms.uOutputSpread.value = THREE.MathUtils.clamp(spreadX, 1, 3.5);
    this.uniforms.uOutputSpreadY.value = THREE.MathUtils.clamp(spreadY, 1, 3.5);
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.beatLevel = smoothBeat(this.beatLevel, features.beat, dt);
    const level = Math.max(features.level, (features.bass + features.mid + features.treble) / 3);
    if (this.visualTime === null) {
      this.time += dt * (0.12 + params.flowSpeed * 0.54 + level * 0.42);
    } else {
      // Wall tiles use the same server epoch. Keep audio in the shape/color
      // uniforms, but derive geometric phase only from shared time so three
      // independently loaded browsers meet perfectly at panel boundaries.
      this.time = this.visualTime * (0.12 + params.flowSpeed * 0.54);
    }

    const activeCount = Math.max(
      4_000,
      Math.min(MAX_PARTICLES, qualityCap(params.qualityTier), Math.round(params.mathParticleCount)),
    );
    this.billboardGeometry.instanceCount = activeCount;

    this.uniforms.uTime.value = this.time;
    this.uniforms.uCount.value = activeCount;
    this.uniforms.uHue.value = ((params.paletteHue % 360) + 360) / 360;
    this.uniforms.uSaturation.value = THREE.MathUtils.clamp(params.saturation, 0, 1);
    this.uniforms.uOpacity.value = THREE.MathUtils.clamp(params.opacity, 0, 1);
    this.uniforms.uIntensity.value = Math.max(0, params.intensity);
    this.uniforms.uPointSize.value = Math.max(0.25, params.mathPointSize);
    this.uniforms.uAmplitude.value = Math.max(0, params.mathAmplitude);
    this.uniforms.uFrequency.value = Math.max(0.2, params.mathFrequency);
    this.uniforms.uRotation.value = Math.max(0, params.mathRotationSpeed);
    this.uniforms.uDepthScale.value = THREE.MathUtils.clamp(params.mathDepthScale, 0, 1.4);
    this.uniforms.uWarmth.value = THREE.MathUtils.clamp(params.mathWarmth, 0, 1);
    this.uniforms.uColorSpread.value = THREE.MathUtils.clamp(params.mathColorSpread, 0, 1);
    this.uniforms.uColorFlow.value = THREE.MathUtils.clamp(params.mathColorFlow, 0, 2);
    this.uniforms.uBass.value = features.bass * params.bassGravity;
    this.uniforms.uMid.value = features.mid * params.midCurl;
    this.uniforms.uTreble.value = features.treble * params.trebleShimmer;
    this.uniforms.uBeat.value = this.beatLevel;
    this.uniforms.uFlow.value = params.flowSpeed;

    const backdropColor = hsl(
      params.paletteHue + params.mathWarmth * 80,
      params.saturation,
      0.11 + this.beatLevel * 0.08,
    );
    if (this.usesLivingFormOptics) {
      const emberColor = hsl(
        params.paletteHue + params.mathColorSpread * 22,
        params.saturation,
        0.18 + this.beatLevel * 0.04,
      );
      backdropColor.lerp(
        emberColor,
        THREE.MathUtils.smoothstep(params.mathWarmth, 0.24, 0.76) *
          (1 - params.mathColorSpread * 0.68),
      );
    }
    const atmosphere = THREE.MathUtils.clamp(params.atmosphere, 0, 1.5);
    const atmosphereGain = this.usesLivingFormOptics
      ? 118 + atmosphere * 118
      : 62 + atmosphere * 72;
    this.backdrop.r = Math.round(backdropColor.r * atmosphereGain + features.bass * 30);
    this.backdrop.g = Math.round(backdropColor.g * atmosphereGain * (this.usesLivingFormOptics ? 0.72 : 0.82) + features.mid * 18);
    this.backdrop.b = Math.round(backdropColor.b * atmosphereGain * (this.usesLivingFormOptics ? 0.55 : 0.76) + features.treble * 12);
    this.backdrop.alpha = THREE.MathUtils.clamp(0.16 + atmosphere * 0.62 + level * 0.16, 0, 1);
    this.backdrop.blurPx = 28 + atmosphere * 38 + level * 18;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.scene.clear();
  }
}
